/**
 * Supported browser / OS matrix for the investor & issuer portals.
 *
 * This is a **single, hand-maintained** configuration object — it is NOT derived
 * from any automatic analysis of the app. To change what we support, edit THIS
 * object and nothing else (EPP-3426).
 *
 * Shape: OS name -> { browser name -> semver range }. The keys are matched by
 * `bowser`'s `satisfies()`:
 *   - OS keys must be bowser OS tokens (`windows`, `macos`, `ios`, `android`).
 *   - Browser keys must be bowser browser names or aliases. Note that Samsung
 *     Internet's detected name is "Samsung Internet for Android"; its only
 *     matching alias is `samsung_internet` (the spaced form does NOT match).
 *
 * `satisfies()` returns `true` when the browser meets the minimum, `false` when
 * it is below it, and `undefined` when the browser/OS is not described here.
 * Only `false` triggers the warning — unknown browsers are left alone.
 *
 * ── Support policy ──────────────────────────────────────────────────────────
 * We support browser versions released within roughly the last year. Each floor
 * is the latest stable release of that browser as of the reference date below,
 * so a browser that has been updated any time in the past ~12 months passes.
 *
 *   Reference date: 2026-07-24 (floors = latest stable as of ~2025-07-24)
 *
 *   Browser           Floor    Released       Next release (just after cutoff)
 *   ----------------- -------- -------------- --------------------------------
 *   chrome            138      2025-06-24     139 on 2025-08-05
 *   edge              138      2025-06-26     139 in late Jul/Aug 2025
 *   firefox           141      2025-07-22     142 on 2025-08-19
 *   safari (macOS)    18.5     2025-05-12     18.6 on 2025-07-29
 *   samsung_internet  28       2025-04 (28.0) 29 on 2025-10-25
 *
 * To roll the window forward, bump each floor to the version that was current
 * ~1 year before the new reference date and update the table above.
 *
 * ── Exception: iOS Safari ────────────────────────────────────────────────────
 * On iOS every browser is WebKit and Safari's major version is locked to the OS
 * version — a device on an older iOS cannot update Safari independently. So the
 * iOS Safari floor mirrors the OLDEST iOS device on the QA matrix rather than the
 * rolling window: QA tests iPhone SE 2022 on iOS 15 (QA device list updated
 * 2026-08-05), so the floor is `>=15`. Auto-updating iOS browsers
 * (Chrome, etc.) still follow the rolling floors above, since on BrowserStack
 * they always run the latest version.
 *
 * Android has no such exception: Chrome and Samsung Internet update from the
 * app store independently of the Android OS version, so even the oldest QA
 * Android device (Samsung S20 on Android 10) runs current browser builds. The
 * matrix therefore gates browser versions only, never Android OS versions.
 */
export type BrowserVersionRules = Record<string, string>;

export const SUPPORTED_BROWSERS: Record<string, BrowserVersionRules> = {
  windows: { chrome: ">=138", firefox: ">=141", edge: ">=138" },
  macos: { safari: ">=18.5", chrome: ">=138" },
  ios: { safari: ">=15", chrome: ">=138" },
  android: { chrome: ">=138", samsung_internet: ">=28" },
};
